import { X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { authoredIconTextPrefix } from '@/components/admin/shared/authored-icon-text'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useSourceContent, useSources } from '../sources/hooks/use-sources'
import type { Task, TaskFormData } from './types'
import { TASK_CATEGORIES } from './types'

interface TaskEditorModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: TaskFormData) => Promise<Task>
  task?: Task | null // If provided, we're editing
  // Pre-fill values when creating from source content context
  initialSourceId?: string | null
  initialSourceContentId?: string | null
}

const initialFormData: TaskFormData = {
  title: '',
  notes: '',
  url: '',
  category: '',
  estimatedMinutes: null,
  sourceId: null,
  sourceContentId: null,
  contentBody: '',
}

export function TaskEditorModal({
  isOpen,
  onClose,
  onSubmit,
  task,
  initialSourceId,
  initialSourceContentId,
}: TaskEditorModalProps) {
  const { sources, loading: sourcesLoading } = useSources()
  const [formData, setFormData] = useState<TaskFormData>(initialFormData)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch source content for selected source
  const { contents: sourceContents, loading: sourceContentsLoading } =
    useSourceContent(formData.sourceId)

  // Initialize form when task changes or modal opens
  useEffect(() => {
    if (isOpen) {
      if (task) {
        // Edit mode - populate from existing task
        setFormData({
          title: task.title,
          notes: task.notes || '',
          url: task.url || '',
          category: task.category || '',
          estimatedMinutes: task.estimatedMinutes,
          sourceId: task.sourceId,
          sourceContentId: task.sourceContentId,
          contentBody: task.contentBody || '',
        })
      } else {
        // Create mode - use initial values if provided
        setFormData({
          ...initialFormData,
          sourceId: initialSourceId || null,
          sourceContentId: initialSourceContentId || null,
        })
      }
      setError(null)
    }
  }, [isOpen, task, initialSourceId, initialSourceContentId])

  // Track previous sourceId to detect changes
  const [prevSourceId, setPrevSourceId] = useState<string | null>(null)

  // Clear sourceContentId when sourceId changes (after initial render)
  useEffect(() => {
    // Skip on initial render
    if (prevSourceId === null && formData.sourceId !== null) {
      setPrevSourceId(formData.sourceId)
      return
    }

    // If sourceId changed
    if (formData.sourceId !== prevSourceId) {
      setPrevSourceId(formData.sourceId)

      // Don't clear if we're in edit mode and this is initial load
      const taskSourceId = task?.sourceId ?? null
      if (formData.sourceId === taskSourceId) {
        return
      }

      // Don't clear if matching initial values in create mode
      if (
        !task &&
        formData.sourceId === initialSourceId &&
        formData.sourceContentId === initialSourceContentId
      ) {
        return
      }

      // Clear sourceContentId when source changes
      if (formData.sourceContentId) {
        setFormData((prev) => ({
          ...prev,
          sourceContentId: null,
        }))
      }
    }
  }, [
    formData.sourceId,
    formData.sourceContentId,
    prevSourceId,
    task,
    initialSourceId,
    initialSourceContentId,
  ])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.title.trim()) {
      setError('Title is required')
      return
    }

    setIsSubmitting(true)
    setError(null)
    try {
      await onSubmit(formData)
      setFormData(initialFormData)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save task')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setFormData(initialFormData)
    setError(null)
    onClose()
  }

  if (!isOpen) return null

  const isEditing = !!task

  return (
    <div className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-card border-border w-full max-w-2xl rounded-lg border p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-foreground text-lg font-semibold">
            {isEditing ? 'Edit Task' : 'Add Task'}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="bg-destructive/10 text-destructive mb-4 rounded-md p-3 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div>
            <label
              htmlFor="task-title"
              className="text-muted-foreground mb-1 block text-sm font-medium"
            >
              Title <span className="text-destructive">*</span>
            </label>
            <Input
              id="task-title"
              type="text"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              placeholder="e.g., Review Big O notation"
              required
            />
          </div>

          {/* Mike's Notes */}
          <div>
            <label
              htmlFor="task-notes"
              className="text-muted-foreground mb-1 block text-sm font-medium"
            >
              Mike's Notes
            </label>
            <textarea
              id="task-notes"
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="Internal notes about the task"
              rows={2}
              className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Category */}
            <div>
              <label
                htmlFor="task-category"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Category
              </label>
              <select
                id="task-category"
                value={formData.category}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
                className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
              >
                <option value="">Select category...</option>
                {TASK_CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Estimated Minutes */}
            <div>
              <label
                htmlFor="task-minutes"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Est. Minutes
              </label>
              <Input
                id="task-minutes"
                type="number"
                min={0}
                value={formData.estimatedMinutes ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    estimatedMinutes: e.target.value
                      ? Number.parseInt(e.target.value, 10)
                      : null,
                  })
                }
                placeholder="e.g., 30"
              />
            </div>
          </div>

          {/* URL */}
          <div>
            <label
              htmlFor="task-url"
              className="text-muted-foreground mb-1 block text-sm font-medium"
            >
              URL
            </label>
            <Input
              id="task-url"
              type="url"
              value={formData.url}
              onChange={(e) =>
                setFormData({ ...formData, url: e.target.value })
              }
              placeholder="https://..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Source */}
            <div>
              <label
                htmlFor="task-source"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Source
              </label>
              <select
                id="task-source"
                value={formData.sourceId || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    sourceId: e.target.value || null,
                    sourceContentId: null, // Clear content when source changes
                  })
                }
                className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
                disabled={sourcesLoading}
              >
                <option value="">No source</option>
                {sources.map((source) => (
                  <option key={source.id} value={source.id}>
                    {authoredIconTextPrefix(source.icon)}
                    {source.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Source Content */}
            <div>
              <label
                htmlFor="task-source-content"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Source Content
              </label>
              <select
                id="task-source-content"
                value={formData.sourceContentId || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    sourceContentId: e.target.value || null,
                  })
                }
                className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
                disabled={!formData.sourceId || sourceContentsLoading}
              >
                <option value="">
                  {!formData.sourceId
                    ? 'Select a source first'
                    : 'No specific content'}
                </option>
                {sourceContents.map((content) => (
                  <option key={content.id} value={content.id}>
                    {content.order > 0 ? `${content.order}. ` : ''}
                    {content.title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Content Body */}
          <div>
            <label
              htmlFor="task-content-body"
              className="text-muted-foreground mb-1 block text-sm font-medium"
            >
              Content Body (Markdown)
            </label>
            <textarea
              id="task-content-body"
              value={formData.contentBody}
              onChange={(e) =>
                setFormData({ ...formData, contentBody: e.target.value })
              }
              placeholder="Markdown content to inject into Notion page body..."
              rows={5}
              className="border-input bg-background font-mono w-full rounded-md border px-3 py-2 text-sm"
            />
            <p className="text-muted-foreground mt-1 text-xs">
              This content will be added to the Notion page when pushed to a
              client's dashboard
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? isEditing
                  ? 'Saving...'
                  : 'Creating...'
                : isEditing
                  ? 'Save Changes'
                  : 'Create Task'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
